import React, { useState, useEffect } from 'react';
import { VStack, HStack, Input, Text, Box } from '@chakra-ui/react';
import Button from '../base/Button';
import { alertManager } from '../../utils/alertManager';
import { useTranslation } from '../../hooks/useTranslation';

interface DateRangePickerProps {
    initialFrom?: Date | null;
    initialTo?: Date | null;
    onApply: (from: Date | null, to: Date | null) => void;
    onReset: () => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ initialFrom, initialTo, onApply, onReset }) => {
    const { t, locale } = useTranslation();
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    // Функция для форматирования даты в локальном формате YYYY-MM-DD
    const formatDateForInput = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Инициализируем значения из текущего фильтра
    useEffect(() => {
        if (initialFrom) {
            const from = new Date(initialFrom);
            setFromDate(formatDateForInput(from));
        } else {
            setFromDate('');
        }
        if (initialTo) {
            const to = new Date(initialTo);
            setToDate(formatDateForInput(to));
        } else {
            setToDate('');
        }
    }, [initialFrom, initialTo]);

    const handleApply = () => {
        let from: Date | null = null;
        let to: Date | null = null;

        if (fromDate) {
            from = new Date(fromDate);
            from.setHours(0, 0, 0, 0);
        }

        if (toDate) {
            to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
        }

        onApply(from, to);
    };

    const handleReset = () => {
        setFromDate('');
        setToDate('');
        onReset();
    };

    return (
        <VStack gap={4} align="stretch" py={4}>
            <HStack gap={3} align="flex-end">
                <Box flex={1}>
                    <Text fontSize="md" color="black" mb={2}>
                        {t.pages.mail.dateFrom}
                    </Text>
                    <Input
                        fontSize="md"
                        color="black"
                        type="date"
                        value={fromDate}
                        onChange={(e) => {
                            const newFromDate = e.target.value;
                            // Валидация: дата "от" не должна быть позднее "до"
                            if (newFromDate && toDate && newFromDate > toDate) {
                                alertManager.error(t.pages.mail.dateFromAfterTo);
                                return;
                            }
                            setFromDate(newFromDate);
                        }}
                        lang={locale}
                        max={toDate || undefined}
                    />
                </Box>
                <Box flex={1}>
                    <Text fontSize="md" color="black" mb={2}>
                        {t.pages.mail.dateTo}
                    </Text>
                    <Input
                        fontSize="md"
                        color="black"
                        type="date"
                        value={toDate}
                        onChange={(e) => {
                            const newToDate = e.target.value;
                            // Валидация: дата "до" не должна быть раньше "от"
                            if (newToDate && fromDate && newToDate < fromDate) {
                                alertManager.error(t.pages.mail.dateToBeforeFrom);
                                return;
                            }
                            setToDate(newToDate);
                        }}
                        lang={locale}
                        min={fromDate || undefined}
                    />
                </Box>
            </HStack>
            <HStack gap={2} justify="flex-end">
                <Button buttonType="danger" onClick={handleReset}>
                    {t.pages.mail.resetButton}
                </Button>
                <Button buttonType="success" onClick={handleApply}>
                    {t.pages.mail.applyButton}
                </Button>
            </HStack>
        </VStack>
    );
};

export default DateRangePicker;
